// Accounts live in Supabase Auth (real server-side password hashing + sessions).
// Usernames are the identity in this app; each maps to an internal email (emailFor).
// `mytv.auth` caches {id, username, role} so the rest of the app can stay synchronous.
// User management (create/delete/password/role) needs the secret service key, so it
// goes through the local server's /api/admin endpoints, which verify the caller is admin.

import { supabase, cloudConfigured, emailFor } from "../cloud/supabase";
import { hydrate, pushNow, setCloudUser } from "../cloud/sync";

export type Role = "admin" | "user";

export interface Auth {
  id: string;
  username: string;
  role: Role;
}
export interface UserRow {
  id: string;
  username: string;
  role: Role;
  created_at: string;
}

const AUTH_KEY = "mytv.auth";
export const MAX_USERS = 5;
type Result = { ok: boolean; error?: string };

export function currentAuth(): Auth | null {
  try {
    const a = JSON.parse(localStorage.getItem(AUTH_KEY) || "null");
    return a && a.id && a.username ? a : null; // ignore old pre-cloud cache entries
  } catch {
    return null;
  }
}
function setAuth(a: Auth | null) {
  if (a) localStorage.setItem(AUTH_KEY, JSON.stringify(a));
  else localStorage.removeItem(AUTH_KEY);
  setCloudUser(a ? { id: a.id, username: a.username } : null);
}
export function isLoggedIn(): boolean {
  return currentAuth() !== null;
}
export function isAdmin(): boolean {
  return currentAuth()?.role === "admin";
}

async function loadProfile(id: string): Promise<Auth | null> {
  const { data, error } = await supabase.from("profiles").select("id, username, role").eq("id", id).maybeSingle();
  if (error || !data) return null;
  return { id: data.id, username: data.username, role: data.role as Role };
}

// At boot: trust the Supabase session, refresh the cached profile, pull cloud data.
export async function restoreSession(): Promise<void> {
  if (!cloudConfigured) return setAuth(null);
  const { data } = await supabase.auth.getSession();
  const user = data.session?.user;
  if (!user) return setAuth(null);
  const profile = (await loadProfile(user.id)) ?? currentAuth();
  if (!profile || profile.id !== user.id) {
    await supabase.auth.signOut();
    return setAuth(null);
  }
  setAuth(profile);
  await hydrate(profile);
}

export async function login(username: string, password: string): Promise<Result> {
  if (!cloudConfigured) return { ok: false, error: "Cloud isn't configured yet (see .env.example)" };
  const { data, error } = await supabase.auth.signInWithPassword({ email: emailFor(username), password });
  if (error || !data.user) return { ok: false, error: error?.message === "Invalid login credentials" ? "Wrong username or password" : error?.message ?? "Login failed" };
  const profile = await loadProfile(data.user.id);
  if (!profile) {
    await supabase.auth.signOut();
    return { ok: false, error: "This account has no profile — ask an admin" };
  }
  setAuth(profile);
  await hydrate(profile);
  return { ok: true };
}

export async function logout() {
  await pushNow(); // don't lose the last edits
  if (cloudConfigured) await supabase.auth.signOut();
  setAuth(null);
}

// ---------------------------------------------------------------- admin API (dev server or Cloudflare Pages Function)
async function adminFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const r = await fetch(`/api/admin${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(init.headers ?? {}) },
  });
  const ct = r.headers.get("content-type") ?? "";
  if (!ct.includes("application/json")) throw new Error("Admin API unavailable — run `npm run dev` locally, or deploy with `npm run deploy` (it includes the admin function).");
  const body = await r.json();
  if (!r.ok) throw new Error(body?.error ?? `HTTP ${r.status}`);
  return body as T;
}
const wrap = async (p: Promise<unknown>): Promise<Result> => {
  try {
    await p;
    return { ok: true };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
};

export interface CloudStatus {
  configured: boolean; // browser has URL + anon key
  serverConfigured: boolean; // local server has the service key
  hasAdmin: boolean;
  dbError?: string; // server has its keys but Supabase didn't answer (e.g. project paused)
}
export async function cloudStatus(): Promise<CloudStatus> {
  if (!cloudConfigured) return { configured: false, serverConfigured: false, hasAdmin: false };
  try {
    const s = await adminFetch<{ serverConfigured: boolean; hasAdmin: boolean; dbError?: string }>("/status");
    return { configured: true, ...s };
  } catch {
    return { configured: true, serverConfigured: false, hasAdmin: true };
  }
}
// First run only: create the first admin (refused once any admin exists).
export const bootstrapAdmin = (username: string, password: string) =>
  wrap(adminFetch("/bootstrap", { method: "POST", body: JSON.stringify({ username, password }) }));

export const listUsers = () => adminFetch<UserRow[]>("/users");
export const createUser = (username: string, password: string, role: Role) =>
  wrap(adminFetch("/users", { method: "POST", body: JSON.stringify({ username, password, role }) }));
export const setPassword = (id: string, password: string) =>
  wrap(adminFetch(`/users/${id}/password`, { method: "POST", body: JSON.stringify({ password }) }));
export const setRole = (id: string, role: Role) => wrap(adminFetch(`/users/${id}/role`, { method: "POST", body: JSON.stringify({ role }) }));
export const deleteUser = (id: string) => wrap(adminFetch(`/users/${id}`, { method: "DELETE" }));

export interface MigrateResult {
  usersCreated: string[];
  usersExisting: string[];
  kvRows: number;
  practiceRecords: number;
  skipped: string[];
}
export const migrateToCloud = (payload: unknown) => adminFetch<MigrateResult>("/migrate", { method: "POST", body: JSON.stringify(payload) });
